import React, {useCallback, useContext, useState, useEffect, useMemo} from 'react';
import {css} from "styled-components/macro"; //eslint-disable-line
import {Accordion, AccordionSummary, AccordionDetails, Alert, Button, Container,
  Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  List, ListItem, ListItemText, ListItemSecondaryAction, Divider,
  Typography, TextField, MenuItem} from '@mui/material';
import {CloudDownload, FolderOpen, UploadFile, Edit, Delete, Add, ExpandMore, PictureAsPdf} from '@mui/icons-material';
import dayjs from 'dayjs';
import AnimationRevealPage from "components/AnimationRevealPage.js";
import Header from "components/Header.js";
import {fetchDocuments, createAndUploadDocument, downloadDocument, updateDocument, uploadDocument, deleteDocument} from "helpers/documents.js";
import AuthContext from 'contexts/AuthContext';

const CATEGORY_OPTIONS = [
  { value: 'bylaws', label: 'Bylaws & Rules' },
  { value: 'financial', label: 'Financial Reports' },
  { value: 'bills', label: 'Bills & Invoices' },
  { value: 'meeting', label: 'Meeting Minutes' },
  { value: 'forms', label: 'Forms & Applications' },
  { value: 'maintenance', label: 'Maintenance Records' },
  { value: 'legal', label: 'Legal Documents' },
  { value: 'other', label: 'Other' }
];

export default function Documents() {
  const {role, designation} = useContext(AuthContext);
  const inputFile = React.useRef(null);
  const [documents, setDocuments] = useState([]);
  const [editDoc, setEditDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);

  // A Treasurer-designated EXECUTIVE has handed Documents off to a Secretary -
  // an undesignated or President-designated EXECUTIVE keeps full access.
  const hasPermission = useMemo(() =>
    role === "ADMIN" || (role === "EXECUTIVE" && designation !== "TREASURER"), [role, designation]);
  const isAdmin = useMemo(() => role === "ADMIN", [role]);

  // Group documents by category
  const groupedDocuments = useMemo(() => {
    const groups = {};
    documents.forEach(doc => {
      const category = doc.category || 'other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(doc);
    });
    return groups;
  }, [documents]);

  const onInputButtonClick = (doc = null) => {
    setUploadingDoc(doc);
    inputFile.current.click();
  };

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // replace with fetchDocuments(setDocuments, category);
      await fetchDocuments(setDocuments);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddDoc = useCallback(() => {
    onInputButtonClick();
  }, []);

  const handleFileSelected = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // If we're uploading for an existing document (re-upload)
    if (uploadingDoc?.id) {
      setLoading(true);
      try {
        await uploadDocument(uploadingDoc.id, file, () => {
          console.log('Document uploaded successfully');
          loadDocuments();
        });
      } catch (err) {
        console.error('Error uploading document:', err);
        setError('Failed to upload document. Please try again.');
      } finally {
        setLoading(false);
        setUploadingDoc(null);
        event.target.value = '';
      }
      return;
    }

    // For new documents - create with file metadata and open dialog
    const newDocument = {
      filename: file.name,
      title: file.name.replace(/\.[^/.]+$/, ""), // Remove file extension for title
      description: "",
      category: "other",
      file_size: file.size,
      mime_type: file.type,
      s3_key: `documents/${file.name}`,
      _selectedFile: file // Keep reference to actual file
    };

    setEditDoc(newDocument);
    setEditDialogOpen(true);
    // Reset file input
    event.target.value = '';
  }, [uploadingDoc, loadDocuments]);

  const handleEditDoc = useCallback((doc) => {
    setEditDoc({...doc});
    setEditDialogOpen(true);
  }, []);

  const handleDeleteClick = useCallback((doc) => {
    setDocToDelete(doc);
    setDeleteDialogOpen(true);
  }, []);

  const handleDownload = useCallback(async (doc) => {
    try {
      await downloadDocument(doc.id);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document. Please try again.');
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!editDoc?.filename || !editDoc?.category) {
      setError('Filename and category are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editDoc.id) {
        // Update existing document metadata only
        const updateRequest = {
          title: editDoc.title,
          description: editDoc.description,
          category: editDoc.category.toLowerCase()
        };
        await updateDocument(editDoc.id, updateRequest);
      } else {
        // Combined upload for new document
        const createRequest = {
          filename: editDoc.filename,
          title: editDoc.title || editDoc.filename,
          description: editDoc.description || "",
          category: editDoc.category.toLowerCase(),
          file_size: editDoc.file_size,
          mime_type: editDoc.mime_type
        };
        // Create document record and upload the file together, atomically
        // (rolled back if the upload fails)
        if (editDoc._selectedFile) {
          await createAndUploadDocument(createRequest, editDoc._selectedFile);
        }
      }
      setEditDialogOpen(false);
      setEditDoc(null);
      await loadDocuments();
    } catch (err) {
      console.error('Error saving document:', err);
      setError('Failed to save document. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editDoc, loadDocuments]);

  const handleDelete = useCallback(async () => {
    if (!docToDelete?.id) return;

    setSaving(true);
    setError(null);
    try {
      await deleteDocument(docToDelete.id);
      setDeleteDialogOpen(false);
      setDocToDelete(null);
      await loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [docToDelete, loadDocuments]);

  const handleCancel = useCallback(() => {
    setEditDialogOpen(false);
    setEditDoc(null);
    setDeleteDialogOpen(false);
    setDocToDelete(null);
  }, []);

  useEffect(() => {
    if (hasPermission) {
      loadDocuments();
    }
  }, [loadDocuments, hasPermission]);

  if (!hasPermission) {
    return (
      <AnimationRevealPage>
        <Header />
        <Container>
          <Alert severity="error">Permission denied</Alert>
        </Container>
      </AnimationRevealPage>
    );
  }

  return (
    <AnimationRevealPage>
      <Header />
      <Container style={{padding: 15, marginTop: 40}}>
        {error && (
          <Alert severity="error" style={{marginBottom: 20}}>
            {error}
          </Alert>
        )}
        <input type="file" ref={inputFile} onChange={handleFileSelected}
          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
          style={{display: 'none'}} />

        <Box display="flex" flexWrap="wrap" rowGap="1rem" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Documents
          </Typography>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddDoc} disabled={loading || saving} >
            Add Document
          </Button>
        </Box>

        {CATEGORY_OPTIONS.map((co, ci) => (
          <Accordion key={ci} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center">
                <FolderOpen color="primary" style={{marginRight: 8}} />
                <Typography variant="h6">
                  {co.label} <small>({(groupedDocuments && groupedDocuments[co.value]) ? groupedDocuments[co.value].length : 0})</small>
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {groupedDocuments[co.value] && <List style={{width: '100%'}}>
                {groupedDocuments[co.value].map((doc, index) => (
                  <React.Fragment key={doc.id}>
                    <ListItem>
                      <Box display="flex" alignItems="center" mr={2}>
                        <PictureAsPdf color="error" />
                      </Box>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1">
                              {doc.title || doc.filename}
                            </Typography>
                            {doc.file_size && (
                              <Chip
                                size="small"
                                label={`${(doc.file_size / 1024 / 1024).toFixed(2)} MB`}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {doc.description && (
                              <Typography variant="body2" color="textSecondary">
                                {doc.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary">
                              Added by {doc.uploaded_by || doc.created_by} on {dayjs(doc.created_at).format('MMM DD, YYYY')}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box display="flex" gap={1}>
                          {doc.s3_key && (
                            <IconButton
                              onClick={() => handleDownload(doc)}
                              color="primary"
                              size="small"
                            >
                              <CloudDownload />
                            </IconButton>
                          )}
                          {!doc.s3_key && doc.id && (
                            <IconButton
                              onClick={() => onInputButtonClick(doc)}
                              color="secondary"
                              size="small"
                            >
                              <UploadFile />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => handleEditDoc(doc)}
                            color="primary"
                            size="small"
                          >
                            <Edit />
                          </IconButton>
                          {isAdmin && (
                            <IconButton
                              onClick={() => handleDeleteClick(doc)}
                              color="error"
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < groupedDocuments[co.value].length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Edit Document Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editDoc?.id ? 'Edit Document' : 'Add New Document'}
          </DialogTitle>
          <DialogContent>
            {editDoc?._selectedFile && (
              <Alert severity="info" style={{marginBottom: 16}}>
                <Typography variant="body2">
                  <strong>Selected file:</strong> {editDoc.filename}
                </Typography>
                <Typography variant="body2">
                  <strong>Size:</strong> {(editDoc.file_size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {editDoc.mime_type}
                </Typography>
              </Alert>
            )}
            <TextField
              margin="dense"
              label="Filename"
              fullWidth
              variant="outlined"
              value={editDoc?.filename || ''}
              onChange={(e) => setEditDoc(prev => ({...prev, filename: e.target.value}))}
              disabled={!!editDoc?._selectedFile}
              helperText={editDoc?._selectedFile ? "Filename from selected file" : ""}
              required
            />
            <TextField
              margin="dense"
              label="Title"
              fullWidth
              variant="outlined"
              value={editDoc?.title || ''}
              onChange={(e) => setEditDoc(prev => ({...prev, title: e.target.value}))}
              helperText="If empty, filename will be used"
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={editDoc?.description || ''}
              onChange={(e) => setEditDoc(prev => ({...prev, description: e.target.value}))}
            />
            <TextField
              select
              margin="dense"
              label="Category"
              fullWidth
              variant="outlined"
              value={editDoc?.category || 'other'}
              onChange={(e) => setEditDoc(prev => ({...prev, category: e.target.value}))}
              required
            >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" disabled={saving}>
              {saving ? 'Saving...' : (editDoc?.id ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCancel}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{docToDelete?.title || docToDelete?.filename}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </AnimationRevealPage>
  );
}
